// src/components/RunDataView.jsx
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { alpha, useTheme } from "@mui/material/styles";
import { getRunData, getRunTables } from "../utils/api";

const ROW_HEIGHT = 40;
/** Max height of the table viewport; larger tables scroll inside this area. */
const MAX_TABLE_VIEW_HEIGHT = 500;
/** Space below the grid when a horizontal scrollbar is shown so it does not cover the last row. */
const HORIZONTAL_SCROLLBAR_GUTTER_PX = 14;
const PAGE_SIZE = 100;
const OVERSCAN = 5;

/** Tables allowed in Detailed data (order = default preference when both exist). */
const DETAIL_DATA_TABLES = ["main", "user_input_data"];

/** After user live prediction runs, BigQuery has this slim table; UI shows only it when present. */
const ULP_DISPLAY_TABLE = "user_live_prediction_display";

/** Format a cell value for display; avoids React rendering arrays/objects as separate nodes. */
function cellDisplayValue(val) {
  if (val == null) return "";
  if (Array.isArray(val)) return val.join(", ");
  if (typeof val === "object") return JSON.stringify(val);
  if (typeof val === "number" && Number.isFinite(val) && !Number.isInteger(val)) {
    return val.toFixed(2);
  }
  if (typeof val === "number" && Number.isFinite(val)) {
    return String(val);
  }
  return String(val);
}

/**
 * Higher percentile = better outcome. Light red if percentile is below 0.2, otherwise light green.
 * Transparent if no percentile column/value.
 * @param {import("@mui/material/styles").Theme} theme
 * @param {Record<string, unknown>} row
 * @param {string[]} columns
 */
function rowBackgroundFromPercentile(theme, row, columns) {
  const pCol = columns.find((c) => String(c).toLowerCase() === "percentile");
  if (!pCol || row == null || typeof row !== "object") return "transparent";
  const raw = row[pCol];
  let n;
  if (typeof raw === "number" && Number.isFinite(raw)) n = raw;
  else n = parseFloat(String(raw ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return "transparent";
  if (n < 0.2) {
    return alpha(theme.palette.error.main, 0.14);
  }
  return alpha(theme.palette.success.main, 0.14);
}

/**
 * Within ``cols``, place names that appear in ``importanceOrder`` first (in that order),
 * then any remaining columns in their original order. Case-insensitive name match.
 */
function reorderSubsetByImportance(cols, importanceOrder) {
  if (!Array.isArray(cols) || cols.length === 0) return cols;
  if (!Array.isArray(importanceOrder) || importanceOrder.length === 0) return cols;
  const lowerToActual = new Map(cols.map((c) => [String(c).toLowerCase(), c]));
  const placed = new Set();
  const front = [];
  for (const raw of importanceOrder) {
    const actual = lowerToActual.get(String(raw).toLowerCase());
    if (actual != null && !placed.has(actual)) {
      front.push(actual);
      placed.add(actual);
    }
  }
  const back = cols.filter((c) => !placed.has(c));
  return [...front, ...back];
}

/**
 * Keep user-upload columns and ``percentile`` fixed; reorder only trailing model feature
 * columns by importance (aligned with GET /data and materialized display table layout).
 */
function applyImportanceColumnOrder(cols, importanceOrder) {
  if (!Array.isArray(cols) || cols.length === 0) return cols;
  if (!Array.isArray(importanceOrder) || importanceOrder.length === 0) return cols;
  const pIdx = cols.findIndex((c) => String(c).toLowerCase() === "percentile");
  if (pIdx < 0) {
    return reorderSubsetByImportance(cols, importanceOrder);
  }
  const head = cols.slice(0, pIdx + 1);
  const tail = cols.slice(pIdx + 1);
  return [...head, ...reorderSubsetByImportance(tail, importanceOrder)];
}

/** @param {{ columnProjection?: string[] | null; columnOrder?: string[] | null; tableName?: string | null; columnExclusion?: string[] | null }} props */
export default function RunDataView({
  runId,
  hideTableSelector = false,
  refreshKey = 0,
  columnProjection = null,
  columnOrder = null,
  tableName = null,
  columnExclusion = null,
}) {
  const theme = useTheme();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [sort, setSort] = useState(null);
  const [order, setOrder] = useState("asc");
  const parentRef = useRef(null);
  const headerRef = useRef(null);
  const scrollInnerContentRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(48);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const headerCellRefs = useRef({});

  const loadTables = useCallback(async () => {
    if (!runId) return;
    setError("");
    // When a specific table is pinned via the tableName prop, skip discovery entirely.
    if (tableName) {
      setTables([tableName]);
      setSelectedTable(tableName);
      return;
    }
    try {
      const data = await getRunTables(runId);
      const list = data?.tables ?? [];
      if (list.includes(ULP_DISPLAY_TABLE)) {
        setTables([ULP_DISPLAY_TABLE]);
        setSelectedTable(ULP_DISPLAY_TABLE);
        return;
      }
      const allowed = DETAIL_DATA_TABLES.filter((t) => list.includes(t));
      setTables(allowed);
      if (allowed.length === 0) {
        setSelectedTable("");
      } else {
        setSelectedTable((prev) =>
          prev && allowed.includes(prev) ? prev : allowed[0]
        );
      }
    } catch (e) {
      setError(e?.message || "Failed to load tables");
      setTables([]);
    }
  }, [runId, tableName]);

  const projectionKey =
    Array.isArray(columnProjection) && columnProjection.length > 0
      ? columnProjection.join("\0")
      : "";

  const importanceOrderKey =
    Array.isArray(columnOrder) && columnOrder.length > 0 ? columnOrder.join("\0") : "";

  const loadPage = useCallback(
    async (offset, append = false) => {
      if (!runId || !selectedTable) return;
      const opts = {
        table: selectedTable,
        limit: PAGE_SIZE,
        offset,
        ...(sort != null && { sort, order }),
        ...(Array.isArray(columnProjection) &&
          columnProjection.length > 0 && { columns: columnProjection }),
      };
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const data = await getRunData(runId, opts);
        const newRows = data?.rows ?? [];
        const totalCount = data?.total ?? 0;
        let cols = data?.columns ?? (newRows[0] ? Object.keys(newRows[0]) : []);
        if (Array.isArray(columnExclusion) && columnExclusion.length > 0) {
          const excludeSet = new Set(columnExclusion.map((c) => String(c).toLowerCase()));
          cols = cols.filter((c) => !excludeSet.has(String(c).toLowerCase()));
        }
        cols = applyImportanceColumnOrder(cols, columnOrder);
        if (append) {
          setRows((prev) => [...prev, ...newRows]);
        } else {
          setRows(newRows);
          setColumns(cols);
        }
        setTotal(totalCount);
        setError("");
      } catch (e) {
        setError(e?.message || "Failed to load data");
        if (!append) setRows([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runId, selectedTable, sort, order, columnOrder, importanceOrderKey, projectionKey]
  );

  useEffect(() => {
    loadTables();
  }, [loadTables, refreshKey]);

  useEffect(() => {
    if (!selectedTable) return;
    // Don't clear rows immediately - keep them visible while loading to avoid blank page
    setTotal(0);
    loadPage(0, false);
  }, [selectedTable, sort, order, loadPage, projectionKey, importanceOrderKey]);

  useEffect(() => {
    setSort(null);
    setOrder("asc");
  }, [projectionKey, importanceOrderKey]);

  const loadingNextRef = useRef(false);
  const handleScroll = useCallback(() => {
    if (loadingMore || loading || rows.length >= total || total === 0) return;
    if (loadingNextRef.current) return;
    const el = parentRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 200;
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      loadingNextRef.current = true;
      const p = loadPage(rows.length, true);
      if (p && typeof p.finally === "function") {
        p.finally(() => {
          loadingNextRef.current = false;
        });
      } else {
        loadingNextRef.current = false;
      }
    }
  }, [loading, loadingMore, rows.length, total, loadPage]);

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => {
      setHeaderHeight(el.getBoundingClientRect().height);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [columns, selectedTable]);

  const gridTemplateColumns = useMemo(() => {
    if (columns.length === 0) return "1fr";
    return columns.map((col) => {
      const width = columnWidths[col];
      return width ? `${width}px` : "minmax(120px, 1fr)";
    }).join(" ");
  }, [columns, columnWidths]);

  const bodyVirtualHeight = virtualizer.getTotalSize();

  useLayoutEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl) return;
    const innerEl = scrollInnerContentRef.current;
    const check = () => {
      const s = parentRef.current;
      if (!s) return;
      setHasHorizontalOverflow(s.scrollWidth > s.clientWidth + 1);
    };
    check();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(check);
    });
    ro.observe(scrollEl);
    if (innerEl) ro.observe(innerEl);
    return () => ro.disconnect();
  }, [gridTemplateColumns, bodyVirtualHeight, rows.length, headerHeight, loading, resizingColumn]);
  const hScrollGutter = hasHorizontalOverflow ? HORIZONTAL_SCROLLBAR_GUTTER_PX : 0;
  const totalContentHeight = headerHeight + bodyVirtualHeight + hScrollGutter;
  const hasMorePages = total > 0 && rows.length < total;
  const needsMaxHeightScroll =
    hasMorePages || totalContentHeight > MAX_TABLE_VIEW_HEIGHT;
  const scrollContainerHeight = needsMaxHeightScroll
    ? MAX_TABLE_VIEW_HEIGHT
    : totalContentHeight;
  const scrollOverflowY = needsMaxHeightScroll ? "auto" : "hidden";

  const handleSort = (col) => {
    if (sort === col) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSort(col);
      setOrder("asc");
    }
  };

  // Column resize handlers
  const handleResizeStart = useCallback((colIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    const col = columns[colIndex];
    if (!col) return;
    
    const startX = e.clientX;
    const startWidth = columnWidths[col] || 120;
    
    setResizingColumn(colIndex);
    
    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(60, startWidth + diff); // Minimum 60px
      
      setColumnWidths((prev) => ({
        ...prev,
        [col]: newWidth,
      }));
    };
    
    const handleMouseUp = () => {
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columns, columnWidths]);

  // Calculate initial column widths based on column name text width
  useEffect(() => {
    if (columns.length === 0) return;
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '600 0.875rem -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    
    setColumnWidths((prev) => {
      // Only update if we need to initialize widths for new columns
      const hasNewColumns = columns.some((col) => !prev[col]);
      if (!hasNewColumns) return prev;
      
      const newWidths = { ...prev };
      columns.forEach((col) => {
        if (!newWidths[col]) {
          // Measure text width + padding + sort icon space
          const textWidth = context.measureText(col).width;
          const padding = 24; // px: 1.5 * 16
          const iconSpace = 24; // space for sort icons
          const minWidth = Math.max(120, textWidth + padding + iconSpace);
          newWidths[col] = minWidth;
        }
      });
      return newWidths;
    });
  }, [columns]);

  if (error && !tables.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Unable to load run data. Please contact support if this persists.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        overflow: "hidden", 
        display: "flex", 
        flexDirection: "column", 
        width: "100%", 
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      {/* Only rendered when there is a table selector to hold: the row count moved
          below the grid, so with the selector hidden this block would otherwise be
          an empty bordered strip. */}
      {!hideTableSelector && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Table</InputLabel>
            <Select
              value={selectedTable}
              label="Table"
              onChange={(e) => setSelectedTable(e.target.value)}
            >
              {tables.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {loading && rows.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={200} />
        </Box>
      ) : (
        <>
          {/* Scrollable container wrapping both header and body */}
          <Box
            ref={parentRef}
            sx={{
              height: scrollContainerHeight,
              maxHeight: MAX_TABLE_VIEW_HEIGHT,
              overflowX: "auto",
              overflowY: scrollOverflowY,
              width: "100%",
              maxWidth: "100%",
              position: "relative",
              minWidth: 0,
              boxSizing: "border-box",
              contain: "layout",
            }}
          >
            {/* Loading overlay when sorting/reloading */}
            {loading && rows.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
            {/* Content wrapper that can expand horizontally but is clipped by parent */}
            <Box
              ref={scrollInnerContentRef}
              sx={{
                minWidth: "max-content",
                width: "max-content",
                paddingBottom: hasHorizontalOverflow ? `${HORIZONTAL_SCROLLBAR_GUTTER_PX}px` : 0,
              }}
            >
              {/* Header row — inside scroll container so horizontal scroll is shared */}
              <Box
                ref={headerRef}
                sx={{
                  display: "grid",
                  gridTemplateColumns: gridTemplateColumns,
                  minWidth: "max-content",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  fontWeight: 600,
                }}
              >
              {columns.map((col, colIndex) => (
                <Box
                  key={col}
                  sx={{
                    display: "flex",
                    position: "relative",
                  }}
                >
                  <Box
                    component="button"
                    onClick={() => handleSort(col)}
                    ref={(el) => {
                      if (el) headerCellRefs.current[col] = el;
                    }}
                    sx={{
                      px: 1.5,
                      py: 1,
                      textAlign: "left",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      bgcolor: sort === col ? "action.selected" : "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 0.5,
                      flex: 1,
                      minWidth: 0,
                      "&:hover": { bgcolor: "action.selected" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <Box component="span" sx={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {col}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        flexShrink: 0,
                      }}
                    >
                      {sort === col ? (
                        order === "asc" ? (
                          <ArrowUpwardIcon sx={{ fontSize: 16, color: "primary.main" }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 16, color: "primary.main" }} />
                        )
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: -0.5,
                            opacity: 0.3,
                          }}
                        >
                          <ArrowUpwardIcon sx={{ fontSize: 12, lineHeight: 0.5 }} />
                          <ArrowDownwardIcon sx={{ fontSize: 12, lineHeight: 0.5, mt: -0.75 }} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {/* Resize handle */}
                  {colIndex < columns.length - 1 && (
                    <Box
                      onMouseDown={(e) => handleResizeStart(colIndex, e)}
                      sx={{
                        position: "absolute",
                        right: -2,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        cursor: "col-resize",
                        zIndex: 2,
                        "&:hover": {
                          bgcolor: "primary.main",
                          opacity: 0.5,
                        },
                        ...(resizingColumn === colIndex && {
                          bgcolor: "primary.main",
                          opacity: 0.7,
                        }),
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>

              {/* Virtualized body */}
              <Box
                sx={{
                  height: `${virtualizer.getTotalSize()}px`,
                  position: "relative",
                }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const row = rows[virtualRow.index];
                  if (!row) return null;
                  return (
                    <Box
                      key={virtualRow.key}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                        display: "grid",
                        gridTemplateColumns: gridTemplateColumns,
                        minWidth: "max-content",
                        width: "max-content",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: rowBackgroundFromPercentile(theme, row, columns),
                      }}
                    >
                      {columns.map((col) => (
                        <Box
                          key={col}
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "0.875rem",
                          }}
                        >
                          {cellDisplayValue(row[col])}
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {loadingMore && (
            <Box sx={{ py: 1, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Loading more…
              </Typography>
            </Box>
          )}

          {/* Row count sits BELOW the grid: it describes the rows just read, and
              every other table on the Live page states its count in the same
              place. */}
          {total > 0 && (
            <Box sx={{ px: 2, pb: 1.5, pt: 1 }}>
              {/* caption, not body2: this is a row count, and it now matches the
                  size every other row count on the Live page uses. */}
              <Typography variant="caption" color="text.secondary">
                {rows.length} of {total} rows
              </Typography>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
